import { createSelector, nanoid } from '@reduxjs/toolkit';
import type { BaseQueryFn, QueryDefinition } from '@reduxjs/toolkit/dist/query';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import type { BaseQueryError, QueryReturnValue } from '@reduxjs/toolkit/dist/query/baseQueryTypes';
import type { QuerySubState, RequestStatusFlags } from '@reduxjs/toolkit/dist/query/core/apiState';
import type { StartQueryActionCreatorOptions } from '@reduxjs/toolkit/dist/query/core/buildInitiate';
import type {
  MutationResultSelectorResult,
  QueryResultSelectorResult,
} from '@reduxjs/toolkit/dist/query/core/buildSelectors';
import type { Recipe } from '@reduxjs/toolkit/dist/query/core/buildThunks';
import type { ApiEndpointMutation, ApiEndpointQuery } from '@reduxjs/toolkit/dist/query/core/module';
import type { BaseEndpointDefinition, MutationDefinition } from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import type { MaybePromise, OmitFromUnion } from '@reduxjs/toolkit/dist/query/tsHelpers';
import { store } from '@stencil/redux';
import { dispatch } from '../../store';
import { requestSlice, requestUtil } from '../requests.slice';

type StartMutationActionCreatorOptions = {
  /**
   * If this mutation should be tracked in the store.
   * If you just want to manually trigger this mutation using `dispatch` and don't care about the
   * result, state & potential errors being held in store, you can set this to false.
   * (defaults to `true`)
   */
  track?: boolean;
  fixedCacheKey?: string;
};

type FetchOptions = StartQueryActionCreatorOptions & StartMutationActionCreatorOptions;

// Create Endpoints

export type Fetch<ArgsType, ResponseType> = (
  params: ArgsType,
  options?: FetchOptions,
) => MaybePromise<QueryReturnValue<ResponseType, BaseQueryError<BaseQueryFn>>>;
export type QueryEndpoint<ArgsType, ResponseType> = Endpoint<ArgsType, ResponseType> & {
  update: (params: ArgsType, updateFn: Recipe<ResponseType>) => void;
};
export type Endpoint<ArgsType, ResponseType> = {
  name: string;
  fetch: Fetch<ArgsType, ResponseType>;
  hook: QueryHook<ArgsType, ResponseType> | MutationHook<ArgsType, ResponseType>;
};
type EndpointDefinitionRules = {
  /**
   * We should use only queryFn
   */
  query?: never;
};

/**
 * For requests that retrieves data, you should use Query.
 */
export function createQueryEndpoint<ArgsType, ResponseType>(
  name: string,
  definition: OmitFromUnion<QueryDefinition<ArgsType, BaseQueryFn, string, ResponseType>, 'type'> &
    EndpointDefinitionRules,
): QueryEndpoint<ArgsType, ResponseType> {
  requestSlice.injectEndpoints({
    endpoints: (builder) => ({
      [name]: builder.query(definition),
    }),
  });

  const endpoint = requestSlice.endpoints[name] as ApiEndpointQuery<any, any>;
  return {
    name,
    fetch: createFetchForQuery<ArgsType, ResponseType>(endpoint, { forceRefetch: true }),
    hook: createHookForQuery<ArgsType, ResponseType>(name, endpoint),
    update: (params, updateFn: Recipe<any>) => {
      return dispatch(requestUtil.updateQueryData(name, params, updateFn));
    },
  };
}

/**
 * For anything that alters data on the server or will possibly invalidate the cache, you should use a Mutation.\
 * Mutations are used to send data updates to the server and apply the changes to the local cache.
 * Mutations can also invalidate cached data and force re-fetches.
 */
export function createMutationEndpoint<ArgsType, ResponseType>(
  name: string,
  definition: OmitFromUnion<MutationDefinition<ArgsType, BaseQueryFn, string, ResponseType>, 'type'> &
    EndpointDefinitionRules,
): Endpoint<ArgsType, ResponseType> {
  requestSlice.injectEndpoints({
    endpoints: (builder) => ({
      [name]: builder.mutation(definition as any),
    }),
  });

  const endpoint = requestSlice.endpoints[name] as ApiEndpointMutation<any, any>;
  return {
    name,
    fetch: createFetch<ArgsType, ResponseType>(endpoint),
    hook: createHookForMutation<ArgsType, ResponseType>(endpoint),
  };
}

function createFetchForQuery<ArgsType, ResponseType>(endpoint, defaultOptions = {}): Fetch<ArgsType, ResponseType> {
  const fetch = createFetch<ArgsType, ResponseType>(endpoint, defaultOptions);
  return (
    params: ArgsType,
    options?: FetchOptions,
  ): MaybePromise<QueryReturnValue<ResponseType, BaseQueryError<BaseQueryFn>>> => {
    const oneStore = store.getStore();
    const endpointCache = endpoint.select(params)(oneStore.getState());

    // Se a mesma query está carregando, deve-se esperar a query em andamento e retornar a sua resposta
    if (endpointCache.isLoading) {
      return new Promise((resolve) => {
        const unsubscribe = oneStore.subscribe(() => {
          const result = endpoint.select(params)(oneStore.getState());
          if (!result.isLoading) {
            unsubscribe();
            resolve(result);
          }
        });
      });
    }

    return fetch(params, options);
  };
}

function createFetch<ArgsType, ResponseType>(endpoint, defaultOptions = {}): Fetch<ArgsType, ResponseType> {
  return (
    params: ArgsType,
    options?: FetchOptions,
  ): MaybePromise<QueryReturnValue<ResponseType, BaseQueryError<BaseQueryFn>>> => {
    const oneStore = store.getStore();
    return oneStore?.dispatch(endpoint.initiate(params, { ...defaultOptions, ...options }));
  };
}

// Create Hooks

export type QueryHook<ArgsType, ResponseType> = () => {
  response: QueryResultSelectorResult<QueryDefinition<ArgsType, BaseQueryFn, string, ResponseType>>;
  meta: {
    params: ArgsType;
    unsubscribe: () => void;
    lastFetchPromise: MaybePromise<
      QuerySubState<BaseEndpointDefinition<ArgsType, BaseQueryFn, ResponseType>> & RequestStatusFlags
    >;
  };
  fetch(
    params: ArgsType,
    options?: StartQueryActionCreatorOptions,
  ): MaybePromise<QuerySubState<BaseEndpointDefinition<ArgsType, BaseQueryFn, ResponseType>> & RequestStatusFlags>;
  refetch(
    options?: FetchOptions,
  ): MaybePromise<QuerySubState<BaseEndpointDefinition<ArgsType, BaseQueryFn, ResponseType>> & RequestStatusFlags>;
  update: (updateFn: Recipe<ResponseType>) => void;
  select(state): any;
};

export type MutationHook<ArgsType, ResponseType> = () => {
  response: MutationResultSelectorResult<MutationDefinition<ArgsType, BaseQueryFn, string, ResponseType>>;
  meta: {
    requestId: string;
    params: ArgsType;
    lastFetchPromise: MaybePromise<
      QuerySubState<BaseEndpointDefinition<ArgsType, BaseQueryFn, ResponseType>> & RequestStatusFlags
    >;
  };
  selectFn: any;
  fetch(
    params: ArgsType,
    options?: StartMutationActionCreatorOptions,
  ): MaybePromise<QuerySubState<BaseEndpointDefinition<ArgsType, BaseQueryFn, ResponseType>> & RequestStatusFlags>;
  select(state): any;
};

function createHookForQuery<ArgsType, ResponseType>(
  name,
  endpoint: ApiEndpointQuery<any, any>,
): QueryHook<ArgsType, ResponseType> {
  return () => {
    const selectFn = createSelectorForQuery<ArgsType>(endpoint);

    const hook: ReturnType<QueryHook<ArgsType, ResponseType>> = {
      response: null,
      meta: {
        params: null as ArgsType,
        unsubscribe: null,
        lastFetchPromise: null,
      },
      select(state) {
        return selectFn(hook.meta.params)(state);
      },
      async fetch(params: ArgsType, options?: FetchOptions) {
        hook.meta.params = params;
        hook.meta.unsubscribe?.();

        const promise = dispatch(endpoint.initiate(params, { forceRefetch: true, ...options }));
        hook.meta.unsubscribe = promise?.unsubscribe;
        hook.meta.lastFetchPromise = promise;
        return promise;
      },
      async refetch(options?: FetchOptions) {
        return hook.fetch(hook.meta.params, { ...options, forceRefetch: true });
      },
      async update(updateFn: Recipe<ResponseType>) {
        return dispatch(requestUtil.updateQueryData(name, hook.meta.params, updateFn));
      },
    };
    return hook;
  };
}

function createHookForMutation<ArgsType, ResponseType>(
  endpoint: ApiEndpointMutation<any, any>,
): MutationHook<ArgsType, ResponseType> {
  const emptyMutationSelector = endpoint.select(skipToken);
  return () => {
    const hook: ReturnType<MutationHook<ArgsType, ResponseType>> = {
      response: null,
      meta: {
        requestId: null as string,
        params: null as ArgsType,
        lastFetchPromise: null,
      },
      selectFn: null,
      select(state) {
        if (!hook.meta.requestId) {
          return emptyMutationSelector(state);
        }
        if (!hook.selectFn) {
          hook.selectFn = endpoint.select(hook.meta.requestId);
        }
        return hook.selectFn(state);
      },
      async fetch(params: ArgsType, options) {
        hook.meta.params = params;
        hook.meta.requestId = nanoid();
        hook.selectFn = null;
        const promise = dispatch(endpoint.initiate(params, { ...options, fixedCacheKey: hook.meta.requestId }));
        hook.meta.lastFetchPromise = promise;
        return promise;
      },
    };
    return hook;
  };
}

// Create Selectors
function createSelectorForQuery<ArgsType>(endpoint): any {
  return createSelector([(query: ArgsType) => query], (query) => endpoint.select(query));
}
